import React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Formik} from "formik";
import {object, string} from "yup";
import Grid from "@mui/material/Grid2";
import {Bolt} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle.js";
import SubmitButton from "./layout/forms/SubmitButton.js";
import Loader from "./layout/loading/loader.js";
import ActeForm from "./referentiel/actes/ActeForm.js";
import ElementMaintenanceField from "./referentiel/actes/ElementMaintenanceField.js";
import Constants from "../../utils/constants.js";
import {toastHelper} from "../../utils/toast-helper.js";
import {
    useGetActeQuery,
    useCreateActeMutation,
    useUpdateActeMutation,
} from "../../modules/actes/actes.api.js";
import {useGetModesOperatoiresQuery} from "../../modules/modesOperatoires/modesOperatoires.api.js";

const schema = object({
    code: string().required("Le code est requis").max(64, "64 caractères max"),
    libelleFr: string().required("Le libellé FR est requis").max(255, "255 caractères max"),
    picto: string().required("L'icône est requise"),
    nature: string().required("La nature est requise"),
    photoRegle: string().required("La règle photo est requise"),
});

export default function ActeNewOrEdit() {
    const {id} = useParams();
    const mode = id ? "edit" : "create";
    const navigate = useNavigate();

    const {data: acte, isLoading} = useGetActeQuery(id, {skip: !id});
    const {data: modesOperatoires = []} = useGetModesOperatoiresQuery();
    const [createActe] = useCreateActeMutation();
    const [updateActe] = useUpdateActeMutation();

    const initialValues = {
        code: acte?.code ?? "",
        libelleFr: acte?.libelleFr ?? "",
        libelleAr: acte?.libelleAr ?? "",
        picto: acte?.picto ?? "Build",
        nature: acte?.nature ?? "BOOLEEN",
        photoRegle: acte?.photoRegle ?? "SI_POSITIF",
        photoMin: acte?.photoMin ?? 1,
        modeOperatoire: acte?.modeOperatoire?.["@id"] ?? null,
    };

    const handleSubmit = async (values, {setSubmitting}) => {
        const payload = {
            ...values,
            photoMin: Number(values.photoMin) || 0,
            modeOperatoire: values.modeOperatoire || null,
        };
        try {
            if (mode === "create") {
                await createActe(payload).unwrap();
                toastHelper.displaySuccess("Acte créé.");
            } else {
                await updateActe({id, ...payload}).unwrap();
                toastHelper.displaySuccess("Acte modifié.");
            }
            // Convention de l'app : après un CRUD, retour à la liste (onglet Actes du référentiel)
            navigate(`${Constants.APP_ROUTE_ADMIN_REFERENTIEL}?tab=actes`);
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        } finally {
            setSubmitting(false);
        }
    };

    if (id && isLoading) return <Loader/>;

    return (
        <>
            <PageTitle title={mode === "create" ? "Nouvel acte" : "Modifier l'acte"} icon={<Bolt/>}/>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                        <ActeForm {...formik} modesOperatoires={modesOperatoires}/>
                        <Grid container justifyContent="center" style={{marginTop: "2rem"}}>
                            <Grid size={4}>
                                <SubmitButton submitting={formik.isSubmitting}/>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>

            {mode === "edit" && acte?.nature === "CHECKLIST" &&
                <ElementMaintenanceField acteId={id} elements={acte?.elements ?? []}/>}
        </>
    );
}
